import { type CommonAction, response } from "@iflyrpa/share";
import { z } from "zod";
import { ActionCommonParamsSchema, AxiosProxyParams } from "../../types";
import { Http } from "../../utils/http";
import { DouyinGetMusicByCategoryParamsSchema } from "./schema";

const { getABogus } = require("../../utils/douyin/douyinSafe/a_bogus.js");
const { generateCsrfTokenAdvanced } = require("../../utils/douyin/douyinSafe/csrfToken.js");
export type DouyinGetMusicByCategoryParams = z.infer<typeof DouyinGetMusicByCategoryParamsSchema>;
export { DouyinGetMusicByCategoryParamsSchema } from "./schema";

export interface Song {
    cover_url: string;
    duration: number;
    music_author: string;
    music_id: string;
    music_name: string;
    play_url: string;
    user_count: number;
}

export interface DouyinCollectionResponse {
    status_code: number;
    status_msg: string;
    songs?: Song[];
    data?: {
        songs?: Song[];
    };
}

export type DouyinGetMusicByCategoryAction = CommonAction<
    DouyinGetMusicByCategoryParams,
    {
        songs: Song[];
    }
>;

export const douyinGetMusicByCategory: DouyinGetMusicByCategoryAction = async (_task, params) => {
    const userAgent =
        params.userAgent ||
        "Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/134.0.0.0 Safari/537.38";

    const headers: any = {
        cookie: params.cookies.map((it: any) => `${it.name}=${it.value}`).join(";"),
        origin: "https://creator.douyin.com",
        referer: "https://creator.douyin.com/",
        Accept: "application/json, text/plain, */*",
        "Accept-Encoding": "gzip, deflate, br, zstd",
        "Content-Type": "application/json",
        pragma: "no-cache",
        "cache-control": "no-cache",
        "sec-ch-ua-platform": params.extraParam?.browserInfo?.["sec-ch-ua-platform"] || "",
        "sec-ch-ua": params.extraParam?.browserInfo?.["sec-ch-ua"] || "",
        "sec-ch-ua-mobile": "?0",
        "user-agent": userAgent,
        "sec-fetch-site": "same-origin",
        "sec-fetch-mode": "cors",
        "sec-fetch-dest": "empty",
        "accept-language": "zh-CN,zh;q=0.9",
        priority: "u=1, i",
    };
    const args: AxiosProxyParams = [{ headers }, _task.logger, params.proxyLoc, params.accountId];
    const http = new Http(...args);

    const csrfToken = generateCsrfTokenAdvanced({
        cookies: params.cookies,
        url: "https://creator.douyin.com/web/api/media/music/list/",
        method: "GET",
        userAgent,
    });

    const collectionParams = {
        status: "0,2",
        count: params.count?.toString() || "20",
        cursor: "0",
        source: "collection_create",
        read_aid: "2906",
        aid: "1128",
        cookie_enabled: "true",
        screen_width: params.extraParam?.browserInfo?.screen_width || "1920",
        screen_height: params.extraParam?.browserInfo?.screen_height || "1080",
        browser_language: params.extraParam?.browserInfo?.browser_language || "zh-CN",
        browser_platform: params.extraParam?.browserInfo?.browser_platform || "Win32",
        browser_name: params.extraParam?.browserInfo?.browser_name || "Mozilla",
        browser_version: userAgent,
        browser_online: "true",
        timezone_name: "Asia/Shanghai",
        support_h265: "1",
        type: params.type || "",
        category_id: params.category_id || "",
        msToken: params.cookies.find((e: any) => e.name === "msToken")?.value || "",
        a_bogus: "",
    };
    const queryParamsBefore = new URLSearchParams(collectionParams).toString();
    const aBogus = getABogus(
        `https://creator.douyin.com/web/api/media/music/list/?${queryParamsBefore}`,
        {},
        userAgent,
    );
    collectionParams.a_bogus = aBogus;
    const queryString = new URLSearchParams(collectionParams).toString();
    const res = await http.api<DouyinCollectionResponse>(
        {
            method: "get",
            url: `https://creator.douyin.com/web/api/media/music/list/?${queryString}`,
            headers: {
                ...headers,
                "X-secsdk-csrf-token": csrfToken,
            },
        },
        { retries: 3, retryDelay: 20, timeout: 3000 },
    );
    const isSuccess = res.status_code === 0;
    const message = `抖音获取推荐音乐${isSuccess ? "成功" : `失败，原因：${res.status_msg}`}${_task.debug ? ` ${http.proxyInfo}` : ""}`;
    const data: { songs: Song[] } = isSuccess ? { songs: res?.songs || [] } : { songs: [] };

    return response(isSuccess ? 0 : 414, message, data);
};
